import { AppStore } from '../AppStore';
import { MegarubigramStore } from '../components/_puzzles/Megarubigram/MegarubigramStore';
import { MorphologyStore } from '../components/_puzzles/Morphology/MorphologyStore';
import { RubigramStore } from '../components/_puzzles/Rubigram/RubigramStore';
import { initRubigram } from '../components/_puzzles/Rubigram/rubigramUtils';
import { initMegarubigram } from '../components/_puzzles/Megarubigram/megarubigramUtils';
import { UltrarubigramStore } from '../components/_puzzles/Ultrarubigram/UltrarubigramStore';
import { initUltrarubigram } from '../components/_puzzles/Ultrarubigram/ultrarubigramUtils';
import { applySavedOrientations, resetOrientations } from '../components/_puzzles/Ultrarubigram/geometry';
import { FeatureData, PuzzleData, PuzzlesProgress } from './types';
import { initVaryClove } from '../components/_puzzles/VaryClove/varyCloveUtils';
import { VaryCloveStore } from '../components/_puzzles/VaryClove/VaryCloveStore';
import { initTriword, useLetterLifeline } from '../components/_puzzles/Triword/triwordUtils';
import { TriwordStore } from '../components/_puzzles/Triword/TriwordStore';
import { initPathword, resetPathwordData, getEnabledHintsForPathword, getEnabledHintsForPathword5x5 } from '../components/_puzzles/Pathword/pathwordUtils';
import { PathwordStore } from '../components/_puzzles/Pathword/PathwordStore';
import { Pathword5x5Store } from '../components/_puzzles/Pathword5x5/Pathword5x5Store';
import { initPathword5x5, resetPathword5x5Data } from '../components/_puzzles/Pathword5x5/pathword5x5utils';
import { initMorphology } from '../components/_puzzles/Morphology/morphologyUtils';
import { initArithmeglyphs } from '../components/_puzzles/Arithmeglyphs/arithmeglyphsUtils';
import { ArithmeglyphsStore } from '../components/_puzzles/Arithmeglyphs/ArithmeglyphsStore';
import { TruthsortingStore } from '../components/_puzzles/Truthsorting/TruthsortingStore';
import { initTruthsorting } from '../components/_puzzles/Truthsorting/truthsortingUtils';

export const initContentData = (puzzleData: PuzzleData, featureData: FeatureData) => {
  // The original puzzle states are preserved here to be used to reset puzzles on logout
  // (Mainly just the rubigram and megarubigram need that.)
  // But wait... doesn't the authstatechanged handler in app.tsx reload everything anyway?
  // Yes but whatever just leave it idc, not important at all
  AppStore.update(s => {
    s.loadingContent = false;
    s.loadingContentError = false;
    s.puzzleData = puzzleData;
    s.featureData = featureData;
  });

  // Get user settings for pathword hints, or use defaults
  const signedIn = AppStore.getRawState().signedIn;
  const userSettings = AppStore.getRawState().userData?.settings;
  const pathwordHints = signedIn && userSettings?.pathwordDefaultHints !== undefined
    ? userSettings.pathwordDefaultHints
    : 2;
  const pathword5x5Hints = signedIn && userSettings?.pathword5x5DefaultHints !== undefined
    ? userSettings.pathword5x5DefaultHints
    : 4;

  initArithmeglyphs(puzzleData.arithmeglyphs);
  initMegarubigram(puzzleData.megarubigram);
  initMorphology(puzzleData.morphology);
  initPathword(puzzleData.pathword, pathwordHints);
  initPathword5x5(puzzleData.pathword5x5, pathword5x5Hints);
  initRubigram(puzzleData.rubigram);
  initTriword(puzzleData.triword);
  initTruthsorting(puzzleData.truthsortingV2);
  initUltrarubigram(puzzleData.ultrarubigram);
  initVaryClove(puzzleData.varyClove);
};

// This clears away any puzzle state from before login by resetting all progress
// or replacing it with any progress saved to the logged-in account.
//
// The onAuthStateChanged code in App.tsx calls initContentData above, which resets
// the puzzle content data, but it doesn't reset progress data.
// E.g. time, solved, user inputs, etc.
// It only updates the actual data loaded from the server like clues and solutions.
//
// Imagine someone not logged-in solved some puzzles at 11:55pm, then logging in at 12:02am.
// The puzzle content would be new, but it would still be marking the puzzles solved
// even though they aren't anymore. This takes care of that and ensures everything is reset.
export const initLoggedInPuzzleSolveStates = (userPuzzlesProgress: PuzzlesProgress) => {
  ArithmeglyphsStore.update(s => {
    if (userPuzzlesProgress.arithmeglyphs) {
      const a = userPuzzlesProgress.arithmeglyphs;
      s.solved = a.solved;
      s.revealed = a.revealed;
      s.time = a.time;
      s.completed = a.completed;
      s.dataByLine.forEach((line, i) => {
        line.guesses = a.partialState?.dataByLine?.[i]?.guesses
          || Array(line.numSeq.length).fill(null);
        line.revealedCells = a.partialState?.dataByLine?.[i]?.revealedCells
          || Array(line.numSeq.length).fill(false);
      });
    } else {
      s.solved = false;
      s.revealed = false;
      s.time = 0;
      s.completed = false;
      s.dataByLine.forEach(line => {
        line.guesses = Array(line.numSeq.length).fill(null);
        line.revealedCells = Array(line.numSeq.length).fill(false);
      });
    }
  });

  UltrarubigramStore.update(s => {
    const u = userPuzzlesProgress.ultrarubigram;
    s.revealed = u?.revealed ?? false;
    s.solved = u?.solved ?? false;
    s.time = u?.time ?? 0;
    s.rot = 0;
    s.isRevealing = false;
    // a finished puzzle is saved with a null partialState; anything done while logged out is cleared
    resetOrientations(s.grids);
    if (u?.partialState && applySavedOrientations(s.grids, u.partialState.grids)) {
      s.rot = u.partialState.rot;
    }
  });

  MegarubigramStore.update(s => {
  if (userPuzzlesProgress.megarubigram) {
    const m = userPuzzlesProgress.megarubigram;
    s.revealed = m.revealed;
    s.solved = m.solved;
    s.time = m.time;
    // if it was solved/revealed, partialState will have been saved as null
    s.grids = m.partialState?.grids || s.grids;
    s.degreesRotated = m.partialState?.degreesRotated || s.degreesRotated;
  } else {
    // clear away anything done while logged-out
    s.revealed = false;
    s.solved = false;
    s.time = 0;
  }
});

  MorphologyStore.update(s => {
    if (userPuzzlesProgress.morphology) {
      const m = userPuzzlesProgress.morphology;
      s.guesses = m.partialState?.guesses || s.clues.map(
        (clue: any) => Array(clue.solution.length).fill(null)
      );
      s.revealedCells = m.partialState?.revealedCells || s.clues.map(
        (clue: any) => Array(clue.solution.length).fill(false)
      );
      s.mistakes = s.clues.map(
        (clue: any) => Array(clue.solution.length).fill(false)
      );
      s.solved = m.solved;
      s.revealed = m.revealed;
      s.completed = m.completed || false;
      s.time = m.time;
    } else {
      s.guesses = s.clues.map(
        (clue: any) => Array(clue.solution.length).fill(null)
      );
      s.revealedCells = s.clues.map(
        (clue: any) => Array(clue.solution.length).fill(false)
      );
      s.mistakes = s.clues.map(
        (clue: any) => Array(clue.solution.length).fill(false)
      );
      s.solved = false;
      s.revealed = false;
      s.completed = false;
      s.time = 0;
    }
  });

  PathwordStore.update(s => {
    if (userPuzzlesProgress.pathword) {
      const p = userPuzzlesProgress.pathword;
      s.revealed = p.revealed;
      s.solved = p.solved;
      s.time = p.time;
    } else {
      // clear away anything done while logged-out
      s.revealed = false;
      s.solved = false;
      s.time = 0;
      resetPathwordData(s);
    }
  });

  Pathword5x5Store.update(s => {
    if (userPuzzlesProgress.pathword5x5) {
      const p = userPuzzlesProgress.pathword5x5;
      s.revealed = p.revealed;
      s.solved = p.solved;
      s.time = p.time;
    } else {
      // clear away anything done while logged-out
      s.revealed = false;
      s.solved = false;
      s.time = 0;
      resetPathword5x5Data(s);
    }
  });

  RubigramStore.update(s => {
    if (userPuzzlesProgress.rubigram) {
      const r = userPuzzlesProgress.rubigram;
      s.revealed = r.revealed;
      s.solved = r.solved;
      s.time = r.time;
    } else {
      // clear away anything done while logged-out
      s.revealed = false;
      s.solved = false;
      s.time = 0;
    }
  });

  TriwordStore.update(s => {
    if (userPuzzlesProgress.triword) {
      const t = userPuzzlesProgress.triword;
      s.revealed = t.revealed;
      s.solved = t.solved;
      s.time = t.time;
      s.used5050Lifeline = t.partialState?.used5050Lifeline || false;
      s.usedLetterLifeline = t.partialState?.usedLetterLifeline || false;
      s.usedPositionLifeline = t.partialState?.usedPositionLifeline || false;
      if (s.usedLetterLifeline) { useLetterLifeline(); }
    } else {
      s.revealed = false;
      s.solved = false;
      s.time = 0;
      s.used5050Lifeline = false;
      s.usedLetterLifeline = false;
      s.usedPositionLifeline = false;
    }
  });

  TruthsortingStore.update(s => {
    if (userPuzzlesProgress.truthsorting) {
      const t = userPuzzlesProgress.truthsorting;
      s.revealed = t.revealed;
      s.solved = t.solved;
      s.time = t.time;
      // more needed here to restore user's partial state
    } else {
      s.revealed = false;
      s.solved = false;
      s.time = 0;
      // more needed here to clear any partial state
    }
  });

  VaryCloveStore.update(s => {
    if (userPuzzlesProgress.varyClove) {
      const v = userPuzzlesProgress.varyClove;
      s.revealed = v.revealed;
      s.solved = v.solved;
      s.time = v.time;
    } else {
      s.revealed = false;
      s.solved = false;
      s.time = 0;
    }
  });
};